// Audit & Compliance Center (Model A: tenant-admin surface in main-app). An
// authorized auditor searches "who changed what, for whom, when, from where, and
// through which channel". The list never shows raw sensitive values; the detail
// drawer reveals before/after only when the backend's permission-gated redaction
// allows it (valuesRedacted otherwise). Tenant scope + RBAC are enforced
// server-side (compliance-service); this page only presents.
import React, { useCallback, useEffect, useState } from 'react';
import { Search, Download, Lock, ChevronDown, ShieldAlert } from 'lucide-react';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '@/lib/apiError';
import { resolvePeopleNames } from '@/lib/hrApi';
import { useHasPermission } from '@/rbac/useRbac';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { Input } from '@/features/shared/components/ui/input';
import { Skeleton } from '@/features/shared/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/features/shared/components/ui/table';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/features/shared/components/ui/sheet';
import { auditService, type AuditFeedEvent, type AuditDetail, type AuditFilters } from '@/services/auditService';

// ── helpers ──────────────────────────────────────────────────────────────────

function fmtTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

// Friendly action label (avoid internal event names in the primary column).
function friendly(action: string, subjectType?: string | null): string {
  const map: Record<string, string> = {
    'salary.updated': 'Salary changed',
    'user.role.changed': 'Role changed',
    'audit.exported': 'Audit exported',
  };
  if (map[action]) return map[action];
  if (subjectType === 'salary') return 'Salary changed';
  // e.g. "employee.updated" → "Employee updated"; "payroll.run.approved" → "Payroll run approved"
  const s = action.replace(/[._]/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function sensitivityVariant(s?: string | null): 'destructive' | 'default' | 'secondary' | 'outline' {
  switch (s) {
    case 'restricted': return 'destructive';
    case 'sensitive': return 'default';
    case 'internal': return 'secondary';
    default: return 'outline';
  }
}

// People, never ids: actor ids are resolved to names through the directory;
// only when a name is genuinely unknown do we fall back to a neutral label.
function actorLabel(e: AuditFeedEvent, names: Record<string, string> = {}): string {
  if (e.actor.agentName) return e.actor.agentName === 'dana' ? 'Dana' : e.actor.agentName;
  if (e.actor.userId) return names[e.actor.userId] || 'Team member';
  return 'System';
}

const MODULE_LABELS: Record<string, string> = {
  // expenses is its own module even though it is served by payroll-service — the
  // people who file and approve claims are not the people who run payroll.
  people: 'People', payroll: 'Payroll', expenses: 'Expenses', leave: 'Leave', performance: 'Performance',
  benefits: 'Benefits', payments: 'Payments', security: 'Security', migration: 'Migration',
  partner: 'Partner', integrations: 'Integrations', compliance: 'Compliance', admin: 'Admin',
};

const EMPTY: AuditFilters = {};

// ── page ─────────────────────────────────────────────────────────────────────

export const AuditCenterPage: React.FC = () => {
  const canView = useHasPermission('audit:view');
  const canExport = useHasPermission('audit:export');

  const [filters, setFilters] = useState<AuditFilters>(EMPTY);
  const [events, setEvents] = useState<AuditFeedEvent[]>([]);
  const [nextCursor, setNextCursor] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [facets, setFacets] = useState<{ modules: string[]; channels: string[]; sensitivities: string[] }>({
    modules: [], channels: [], sensitivities: [],
  });

  const [selected, setSelected] = useState<AuditDetail | null>(null);
  const [names, setNames] = useState<Record<string, string>>({});
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const load = useCallback(async (reset: boolean, cursor?: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await auditService.list({ ...filters, cursor: reset ? undefined : cursor, limit: 50 });
      setEvents((prev) => (reset ? res.data : [...prev, ...res.data]));
      setNextCursor(res.nextCursor);
      const ids = res.data.flatMap((e) => [e.actor.userId, e.actor.onBehalfOf]).filter((x): x is string => !!x);
      if (ids.length) {
        const resolved = await resolvePeopleNames({ userIds: ids });
        setNames((prev) => ({ ...prev, ...resolved }));
      }
    } catch (e: any) {
      if (e?.response?.status === 403) setError('You don’t have access to the Audit & Compliance Center.');
      else setError(getApiErrorMessage(e, 'Could not load the audit trail.'));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    if (!canView) return;
    auditService.facets().then(setFacets).catch(() => { /* filter bar just stays empty */ });
  }, [canView]);

  useEffect(() => {
    if (!canView) return;
    load(true);
  }, [canView, load]);

  const openDetail = async (id: string) => {
    setDrawerOpen(true);
    setLoadingDetail(true);
    setSelected(null);
    try {
      setSelected(await auditService.detail(id));
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not load event detail.'));
      setDrawerOpen(false);
    } finally {
      setLoadingDetail(false);
    }
  };

  const doExport = async () => {
    try {
      const blob = await auditService.exportCsv(filters);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'audit-export.csv';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Export started');
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Export failed.'));
    }
  };

  const set = (patch: Partial<AuditFilters>) => setFilters((f) => ({ ...f, ...patch }));

  const selStyle = 'h-9 rounded-md border border-input bg-background px-2 text-sm';

  // Any user-set filter (ignoring pagination) — so an empty result can be phrased
  // as "no match for your filters" vs. "nothing recorded yet".
  const hasActiveFilters = Object.entries(filters).some(
    ([k, v]) => k !== 'limit' && k !== 'cursor' && v != null && v !== '',
  );

  if (!canView) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="flex items-center gap-3 py-8 text-muted-foreground">
            <Lock className="w-5 h-5" />
            <div>
              <div className="font-medium text-foreground">Not available</div>
              The Audit &amp; Compliance Center is for auditors and compliance officers.
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-muted-foreground" /> Audit &amp; Compliance
          </h1>
          <p className="text-sm text-muted-foreground">Who changed what, for whom, when, and through which channel. Sensitive values are shown only to authorized roles.</p>
        </div>
        {canExport && (
          <Button variant="outline" onClick={doExport} className="gap-2">
            <Download className="w-4 h-4" /> Export CSV
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="flex flex-wrap items-end gap-2 py-3">
          <label className="flex flex-col text-xs text-muted-foreground gap-1">From
            <Input type="date" className="h-9 w-40" value={filters.from?.slice(0, 10) ?? ''}
              onChange={(e) => set({ from: e.target.value ? new Date(e.target.value).toISOString() : undefined })} />
          </label>
          <label className="flex flex-col text-xs text-muted-foreground gap-1">To
            <Input type="date" className="h-9 w-40" value={filters.to?.slice(0, 10) ?? ''}
              onChange={(e) => set({ to: e.target.value ? new Date(e.target.value + 'T23:59:59').toISOString() : undefined })} />
          </label>
          <label className="flex flex-col text-xs text-muted-foreground gap-1">Module
            <select className={selStyle} value={filters.module ?? ''} onChange={(e) => set({ module: e.target.value || undefined })}>
              <option value="">All</option>
              {facets.modules.map((m) => <option key={m} value={m}>{MODULE_LABELS[m] ?? m}</option>)}
            </select>
          </label>
          <label className="flex flex-col text-xs text-muted-foreground gap-1">Channel
            <select className={selStyle} value={filters.channel ?? ''} onChange={(e) => set({ channel: e.target.value || undefined })}>
              <option value="">All</option>
              {facets.channels.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label className="flex flex-col text-xs text-muted-foreground gap-1">Sensitivity
            <select className={selStyle} value={filters.sensitivity ?? ''} onChange={(e) => set({ sensitivity: e.target.value || undefined })}>
              <option value="">All</option>
              {facets.sensitivities.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="flex flex-col text-xs text-muted-foreground gap-1 flex-1 min-w-[160px]">Search
            <Input className="h-9" placeholder="salary, role changed, bank…" value={filters.q ?? ''}
              onChange={(e) => set({ q: e.target.value || undefined })}
              onKeyDown={(e) => { if (e.key === 'Enter') load(true); }} />
          </label>
          <Button onClick={() => load(true)} className="gap-2 h-9"><Search className="w-4 h-4" /> Search</Button>
          {Object.keys(filters).length > 0 && (
            <Button variant="ghost" className="h-9" onClick={() => setFilters(EMPTY)}>Clear</Button>
          )}
        </CardContent>
      </Card>

      {/* Feed */}
      <Card>
        <CardContent className="p-0">
          {error ? (
            <div className="p-6 text-sm text-danger">{error}</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>Actor</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Module</TableHead>
                  <TableHead>Channel</TableHead>
                  <TableHead>Sensitivity</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && events.length === 0 ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <TableRow key={i}><TableCell colSpan={7}><Skeleton className="h-5 w-full" /></TableCell></TableRow>
                  ))
                ) : events.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="py-12">
                      <div className="mx-auto max-w-md text-center text-muted-foreground space-y-1">
                        {hasActiveFilters ? (
                          <>
                            <div className="font-medium text-foreground">No matching audit activity</div>
                            <div>No events match your current filters. Try widening the date range or clearing filters.</div>
                          </>
                        ) : (
                          <>
                            <div className="font-medium text-foreground">No audit activity recorded yet</div>
                            <div>
                              You have permission to view the audit log — there’s simply nothing to show yet.
                              Changes across the workspace (who changed what, when, and through which channel)
                              will appear here as they happen.
                            </div>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  events.map((e) => (
                    <TableRow key={e.id} className="cursor-pointer" onClick={() => openDetail(e.id)}>
                      <TableCell className="whitespace-nowrap tabular-nums text-muted-foreground">{fmtTime(e.occurredAt)}</TableCell>
                      <TableCell>{actorLabel(e, names)}{e.actor.onBehalfOf ? <span className="text-xs text-muted-foreground"> (delegated)</span> : null}</TableCell>
                      <TableCell className="font-medium">{friendly(e.action, e.subjectType)}</TableCell>
                      <TableCell className="text-muted-foreground">{e.subjectType ?? '—'}</TableCell>
                      <TableCell>{e.module ? (MODULE_LABELS[e.module] ?? e.module) : '—'}</TableCell>
                      <TableCell>{e.channel ?? '—'}</TableCell>
                      <TableCell><Badge variant={sensitivityVariant(e.sensitivity)}>{e.sensitivity ?? 'low'}</Badge></TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
          {nextCursor && (
            <div className="p-3 text-center border-t">
              <Button variant="ghost" disabled={loading} onClick={() => load(false, nextCursor)} className="gap-1">
                <ChevronDown className="w-4 h-4" /> {loading ? 'Loading…' : 'Load more'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{selected ? friendly(selected.action, selected.subjectType) : 'Audit event'}</SheetTitle>
            <SheetDescription>{selected ? fmtTime(selected.occurredAt) : ''}</SheetDescription>
          </SheetHeader>
          {loadingDetail || !selected ? (
            <div className="space-y-3 mt-4"><Skeleton className="h-6 w-2/3" /><Skeleton className="h-24 w-full" /></div>
          ) : (
            <div className="mt-4 space-y-4 text-sm">
              <Field label="Actor" value={`${actorLabel(selected, names)}${selected.actor.actorType ? ` · ${selected.actor.actorType}` : ''}`} />
              {selected.actor.onBehalfOf && <Field label="Delegated by" value={names[selected.actor.onBehalfOf] || 'Team member'} />}
              <Field label="Subject" value={`${selected.subjectType ?? '—'}${selected.subjectId ? ` · ${selected.subjectId}` : ''}`} />
              <Field label="Module" value={selected.module ? (MODULE_LABELS[selected.module] ?? selected.module) : '—'} />
              <Field label="Channel" value={selected.channel ?? '—'} />
              <Field label="Sensitivity" value={selected.sensitivity ?? 'low'} />
              {selected.changedFields?.length ? <Field label="Changed fields" value={selected.changedFields.join(', ')} /> : null}
              {selected.reason && <Field label="Reason" value={selected.reason} />}
              {selected.correlationId && <Field label="Correlation ID" value={selected.correlationId} mono />}
              <Field label="Service" value={selected.sourceService ?? '—'} />

              <div className="pt-2 border-t">
                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Before → After</div>
                {selected.valuesRedacted ? (
                  <div className="flex items-center gap-2 text-muted-foreground bg-muted/40 rounded-md p-3">
                    <Lock className="w-4 h-4" /> Values are restricted. You can see that a change occurred; revealing the values requires elevated audit permission.
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    <pre className="bg-muted/40 rounded-md p-2 text-xs overflow-auto max-h-64">{JSON.stringify(selected.before ?? null, null, 2)}</pre>
                    <pre className="bg-muted/40 rounded-md p-2 text-xs overflow-auto max-h-64">{JSON.stringify(selected.after ?? null, null, 2)}</pre>
                  </div>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

const Field: React.FC<{ label: string; value: string; mono?: boolean }> = ({ label, value, mono }) => (
  <div className="flex justify-between gap-4">
    <span className="text-muted-foreground">{label}</span>
    <span className={`text-right ${mono ? 'font-mono text-xs' : ''}`}>{value}</span>
  </div>
);

export default AuditCenterPage;
